import { api } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";
import { clearTokens, setTokens } from "@/lib/auth/tokens";
import type { components } from "@/lib/api/schema.gen";
import type { CurrentUser } from "@/lib/auth/roles";
import type { Language } from "@/lib/i18n/i18n";

/** Exchange credentials for a JWT pair and store them. */
export async function login(credentials: {
  email: string;
  password: string;
}): Promise<void> {
  // drf-spectacular models the token endpoint's request and response with one
  // schema, so the generated request type lists the read-only `access`/`refresh`
  // as required. We only send credentials — cast to satisfy the body type.
  const tokens = await unwrap(
    api.POST("/api/v1/auth/token/", {
      body: credentials as components["schemas"]["TokenObtainPair"],
    }),
  );
  setTokens({ access: tokens.access, refresh: tokens.refresh });
}

/**
 * Log out locally. simplejwt's blacklist endpoint isn't exposed, so this clears
 * the client-held tokens; the short access-token lifetime (15 min) bounds the
 * window. Wire a blacklist call here if/when the backend adds one.
 */
export function logout(): void {
  clearTokens();
}

/** Fetch the current user (drives role guards and the shell). */
export async function fetchMe(): Promise<CurrentUser> {
  // The generated schema under-types MeSerializer's SerializerMethodFields as
  // `string` (no backend type hints), so cast to the canonical CurrentUser.
  // See src/lib/api/types.ts.
  const me = await unwrap(api.GET("/api/v1/auth/me/"));
  return me as unknown as CurrentUser;
}

/**
 * Update the current user's respondent identity (first/last name, position)
 * or the language they chose in the switcher.
 */
export async function patchMe(patch: {
  first_name?: string;
  last_name?: string;
  position?: string;
  language?: Language;
}): Promise<CurrentUser> {
  const me = await unwrap(
    api.PATCH("/api/v1/auth/me/", {
      body: patch as components["schemas"]["PatchedMe"],
    }),
  );
  return me as unknown as CurrentUser;
}

/**
 * Change the current user's password. The endpoint's serializer is untyped in
 * the generated schema (no request body), so the init is cast — the runtime
 * payload is `old_password` + `new_password1`/`new_password2`. On validation
 * failure the thrown ApiError carries per-field messages.
 */
export async function changePassword(payload: {
  old_password: string;
  new_password1: string;
  new_password2: string;
}): Promise<void> {
  await unwrap(
    api.POST("/api/v1/auth/password/change/", { body: payload } as never),
  );
}

/**
 * Create an account. On success the backend sends an allauth verification email
 * (verification is mandatory) and returns no token — the caller shows a
 * "check your email" state; the user verifies via the emailed link, then signs
 * in. A 400 carries per-field errors (`email`, `password1`, `password2`).
 */
export async function register(payload: {
  email: string;
  password1: string;
  password2: string;
  /** Not collected at signup (A-7 — captured at identification); optional here. */
  first_name?: string;
  last_name?: string;
  /** Present when arriving via an invitation link — auto-accepted at register (A-5). */
  invitation_token?: string;
}): Promise<void> {
  await unwrap(api.POST("/api/v1/auth/register/", { body: payload } as never));
}

/**
 * Confirm an email address from the `key` in the verification link. The SPA
 * signup confirmation email points at `/verify-email?key=…` (see the Django
 * `CustomAccountAdapter`); this posts that key to the allauth-backed verify
 * endpoint. A 400 carries the detail for an invalid/expired key.
 */
export async function verifyEmail(key: string): Promise<void> {
  await unwrap(
    api.POST("/api/v1/auth/email/verify/{key}/", {
      params: { path: { key } },
    }),
  );
}

/**
 * Request a password-reset email. The endpoint always succeeds (it never
 * reveals whether the address has an account — anti-enumeration), so callers
 * show the same "check your email" state regardless.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await unwrap(
    api.POST("/api/v1/auth/password/reset/", { body: { email } } as never),
  );
}

/**
 * Set a new password from the emailed link's `uid` + `token`. On an invalid or
 * expired link the thrown ApiError carries the detail; per-field password
 * errors surface under `new_password1`/`new_password2`.
 */
export async function confirmPasswordReset(payload: {
  uid: string;
  token: string;
  new_password1: string;
  new_password2: string;
}): Promise<void> {
  await unwrap(
    api.POST("/api/v1/auth/password/reset/confirm/", { body: payload } as never),
  );
}
